import React, { useEffect, useCallback, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box, TextField, Select, MenuItem, FormControl, InputLabel, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, CircularProgress, Typography, InputAdornment,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material'
import { Search, Play, RefreshCw, Eye, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  fetchBoList, selectBo, startCleaning,
  setListPage, setListFilter, setListSearch, setListLanguage, setListDateRange, syncFromSgg,
} from '../../../../redux/actions/boConsolidationAction'
import { backendClient } from '../../../../api/backend'

const STATUS_COLORS = {
  pending: 'default',
  cleaning: 'info',
  review: 'warning',
  completed: 'success',
  failed: 'error',
}

export default function BOListTable() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { boList, phase, sync = { loading: false, result: null, error: null } } = useSelector((s) => s.boConsolidation)
  const { data, loading, total, page, pageSize, statusFilter, search, language, dateFrom, dateTo } = boList
  const [snackbar, setSnackbar] = useState(null)
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: null, loading: false, boNum: '' })

  const handlePreview = async (bo) => {
    setPdfPreview({ open: true, url: null, loading: true, boNum: bo.bo_num })
    try {
      const res = await backendClient.getBoPdfUrl(bo.id)
      setPdfPreview({ open: true, url: res.pdf_url, loading: false, boNum: bo.bo_num })
    } catch {
      setPdfPreview({ open: false, url: null, loading: false, boNum: '' })
      setSnackbar({ severity: 'error', message: t('du_bo_pdf_error', 'Failed to load PDF') })
    }
  }

  useEffect(() => {
    if (!sync.loading && sync.result) {
      setSnackbar({
        severity: 'success',
        message: t('du_bo_sync_success',
          `Added ${sync.result.new_inserted} new BOs (FR: ${sync.result.total_fr}, AR: ${sync.result.total_ar})`,
          { new_inserted: sync.result.new_inserted, total_fr: sync.result.total_fr, total_ar: sync.result.total_ar }
        ),
      })
    } else if (!sync.loading && sync.error) {
      setSnackbar({ severity: 'error', message: sync.error })
    }
  }, [sync.loading, sync.result, sync.error])

  const loadData = useCallback(() => {
    dispatch(fetchBoList(page, pageSize, statusFilter, search, language, dateFrom, dateTo))
  }, [dispatch, page, pageSize, statusFilter, search, language, dateFrom, dateTo])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSearchChange = (e) => {
    dispatch(setListSearch(e.target.value))
  }

  const handleFilterChange = (e) => {
    dispatch(setListFilter(e.target.value))
  }

  const handleLanguageChange = (e) => {
    dispatch(setListLanguage(e.target.value))
  }

  const handleDateFromChange = (e) => {
    dispatch(setListDateRange(e.target.value, dateTo))
  }

  const handleDateToChange = (e) => {
    dispatch(setListDateRange(dateFrom, e.target.value))
  }

  const handlePageChange = (_, newPage) => {
    dispatch(setListPage(newPage + 1))
  }

  const handleProcess = (bo) => {
    dispatch(selectBo(bo))
    dispatch(startCleaning(bo.id))
  }

  const isProcessing = phase === 'cleaning' || phase === 'executing'

  return (
    <Box>
      {/* Filters row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('du_bo_search_placeholder', 'Search by BO number...')}
          value={search}
          onChange={handleSearchChange}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color="#999" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('du_bo_status', 'Status')}</InputLabel>
          <Select value={statusFilter} label={t('du_bo_status', 'Status')} onChange={handleFilterChange}>
            <MenuItem value="all">{t('du_bo_all', 'All')}</MenuItem>
            <MenuItem value="pending">{t('du_bo_pending', 'Pending')}</MenuItem>
            <MenuItem value="review">{t('du_bo_review', 'Review')}</MenuItem>
            <MenuItem value="completed">{t('du_bo_completed', 'Completed')}</MenuItem>
            <MenuItem value="failed">{t('du_bo_failed_status', 'Failed')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('du_bo_language', 'Language')}</InputLabel>
          <Select value={language} label={t('du_bo_language', 'Language')} onChange={handleLanguageChange}>
            <MenuItem value="all">{t('du_bo_all', 'All')}</MenuItem>
            <MenuItem value="fr">FR</MenuItem>
            <MenuItem value="ar">AR</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          type="date"
          label={t('du_bo_date_from', 'From')}
          value={dateFrom}
          onChange={handleDateFromChange}
          sx={{ minWidth: 150 }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          size="small"
          type="date"
          label={t('du_bo_date_to', 'To')}
          value={dateTo}
          onChange={handleDateToChange}
          sx={{ minWidth: 150 }}
          InputLabelProps={{ shrink: true }}
        />

        <Button
          variant="outlined"
          size="small"
          startIcon={sync.loading ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
          disabled={sync.loading}
          onClick={() => dispatch(syncFromSgg())}
          sx={{
            textTransform: 'none',
            borderColor: '#c9a96e',
            color: '#c9a96e',
            '&:hover': { borderColor: '#b8944f', backgroundColor: 'rgba(201,169,110,0.04)' },
          }}
        >
          {t('du_bo_sync_sgg', 'Sync from SGG')}
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#fafafa' } }}>
              <TableCell>{t('du_bo_number', 'BO Number')}</TableCell>
              <TableCell>{t('du_bo_date', 'Date')}</TableCell>
              <TableCell>{t('du_bo_language', 'Language')}</TableCell>
              <TableCell>{t('du_bo_status', 'Status')}</TableCell>
              <TableCell align="right">{t('du_bo_actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} sx={{ color: '#c9a96e' }} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('du_bo_no_results', 'No BOs found')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((bo) => (
                <TableRow key={bo.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {bo.bo_num}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {bo.bo_date ? new Date(bo.bo_date).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(bo.language || 'fr').toUpperCase()}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`du_bo_status_${bo.processing_status}`, bo.processing_status || 'pending')}
                      size="small"
                      color={STATUS_COLORS[bo.processing_status] || 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(bo)}
                        sx={{ color: '#c9a96e', '&:hover': { backgroundColor: 'rgba(201,169,110,0.08)' } }}
                        title={t('du_bo_preview_pdf', 'Preview PDF')}
                      >
                        <Eye size={16} />
                      </IconButton>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Play size={14} />}
                        disabled={isProcessing || bo.processing_status === 'cleaning' || bo.processing_status === 'executing'}
                        onClick={() => handleProcess(bo)}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#c9a96e',
                          color: '#c9a96e',
                          '&:hover': { borderColor: '#b8944f', backgroundColor: 'rgba(201,169,110,0.04)' },
                        }}
                      >
                        {t('du_bo_process', 'Process')}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {total > pageSize && (
          <TablePagination
            component="div"
            count={total}
            page={page - 1}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            onPageChange={handlePageChange}
          />
        )}
      </TableContainer>

      {/* PDF Preview Modal */}
      <Dialog
        open={pdfPreview.open}
        onClose={() => setPdfPreview({ open: false, url: null, loading: false, boNum: '' })}
        maxWidth={false}
        PaperProps={{ sx: { width: '80vw', height: '90vh', maxWidth: '1200px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Typography variant="h6">BO {pdfPreview.boNum}</Typography>
          <IconButton onClick={() => setPdfPreview({ open: false, url: null, loading: false, boNum: '' })} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {pdfPreview.loading ? (
            <CircularProgress sx={{ color: '#c9a96e' }} />
          ) : pdfPreview.url ? (
            <iframe
              src={pdfPreview.url}
              title={`BO ${pdfPreview.boNum} PDF`}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
